import React, { useState, useEffect } from 'react';
import { calendarSyncAPI } from '../lib/api';
import { Layout } from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Download, Link as LinkIcon, Copy, CheckCircle2, CalendarPlus, Unplug, RefreshCw, Loader2, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

export default function CalendarSync() {
  const [status, setStatus] = useState(null);
  const [events, setEvents] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pushing, setPushing] = useState(false);
  const [pulling, setPulling] = useState(false);
  const [icsUrl, setIcsUrl] = useState('');

  const loadStatus = async () => {
    try {
      const [s, u] = await Promise.all([
        calendarSyncAPI.googleStatus(),
        calendarSyncAPI.getSubscriptionUrl().catch(() => null),
      ]);
      setStatus(s.data);
      if (u?.data?.url) setIcsUrl(u.data.url);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    loadStatus();
    // Check if we just got redirected back from Google
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('google_calendar') === 'connected') {
      toast.success('Google Calendar connected!');
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const copyIcs = () => {
    navigator.clipboard.writeText(icsUrl);
    toast.success('Link copied — paste it in Google/Apple Calendar → "Subscribe from URL"');
  };

  const downloadIcs = async () => {
    try {
      const res = await calendarSyncAPI.downloadIcs();
      const blob = new Blob([res.data], { type: 'text/calendar' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = 'the_eleventh_tasks.ics'; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error('Download failed'); }
  };

  const connectGoogle = async () => {
    try {
      const r = await calendarSyncAPI.googleAuthorize();
      window.location.href = r.data.authorization_url;
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const disconnectGoogle = async () => {
    if (!window.confirm('Disconnect Google Calendar?')) return;
    try { await calendarSyncAPI.googleDisconnect(); toast.success('Disconnected'); loadStatus(); }
    catch (e) { toast.error('Failed'); }
  };

  const pushNow = async () => {
    setPushing(true);
    try {
      const r = await calendarSyncAPI.googlePush();
      toast.success(`Pushed ${r.data.pushed} tasks${r.data.failed ? ` (${r.data.failed} failed)` : ''}`);
      loadStatus();
    } catch (e) { toast.error(e.response?.data?.detail || 'Push failed'); }
    finally { setPushing(false); }
  };

  const pullEvents = async () => {
    setPulling(true);
    try {
      const r = await calendarSyncAPI.googlePull();
      setEvents(r.data.events);
      toast.success(`Fetched ${r.data.count} events`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Pull failed'); }
    finally { setPulling(false); }
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="calendar-sync-page">
        <div>
          <h1 className="text-2xl sm:text-3xl font-light tracking-tight flex items-center gap-3">
            <CalendarPlus className="h-7 w-7 text-emerald-500" />Calendar Sync
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Keep your tasks & deadlines in sync with Google Calendar, Apple Calendar, and Outlook</p>
        </div>

        <Card>
          <CardContent className="p-6 space-y-3">
            <div className="flex items-start gap-3">
              <LinkIcon className="h-5 w-5 text-sky-500 mt-1" />
              <div className="flex-1">
                <div className="font-medium">Subscribe via .ics link (read-only)</div>
                <p className="text-sm text-muted-foreground mt-1">
                  Works in Google Calendar ("From URL"), Apple Calendar ("New Calendar Subscription"), and Outlook. Updates automatically.
                </p>
                <div className="flex gap-2 mt-3">
                  <Input readOnly value={icsUrl} className="font-mono text-xs" data-testid="ics-url" />
                  <Button variant="outline" onClick={copyIcs} data-testid="copy-ics-btn"><Copy className="h-4 w-4 mr-1" />Copy</Button>
                  <Button variant="outline" onClick={downloadIcs} data-testid="download-ics-btn"><Download className="h-4 w-4 mr-1" />Download</Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-3">
              <CalendarPlus className="h-5 w-5 text-violet-500" />
              <div className="flex-1">
                <div className="font-medium">Google Calendar — 2-way sync</div>
                <p className="text-sm text-muted-foreground">Push your tasks as events and pull upcoming meetings</p>
              </div>
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> :
                status?.connected ? <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-1 rounded-full flex items-center gap-1"><CheckCircle2 className="h-3 w-3" />Connected</span> :
                  <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded-full">Not connected</span>
              }
            </div>

            {!loading && !status?.configured && (
              <div className="p-3 rounded bg-amber-50 text-amber-800 text-sm" data-testid="google-not-configured">
                <div className="font-medium mb-1">Setup required</div>
                An admin needs to add <code className="px-1 bg-amber-100 rounded">GOOGLE_CLIENT_ID</code> and <code className="px-1 bg-amber-100 rounded">GOOGLE_CLIENT_SECRET</code> to the backend environment. See Google Cloud Console → OAuth credentials.
              </div>
            )}

            {!loading && status?.configured && !status?.connected && (
              <Button onClick={connectGoogle} data-testid="connect-google-btn"><CalendarPlus className="h-4 w-4 mr-2" />Connect Google Calendar</Button>
            )}

            {!loading && status?.connected && (
              <div className="space-y-3">
                <div className="text-sm">Connected as <span className="font-medium">{status.google_email}</span>{status.last_synced_at && <span className="text-muted-foreground"> · Last synced {new Date(status.last_synced_at).toLocaleString()}</span>}</div>
                <div className="flex gap-2 flex-wrap">
                  <Button onClick={pushNow} disabled={pushing} data-testid="push-btn">{pushing ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1" />}Push My Tasks</Button>
                  <Button variant="outline" onClick={pullEvents} disabled={pulling} data-testid="pull-btn">{pulling ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <ExternalLink className="h-4 w-4 mr-1" />}Pull Upcoming</Button>
                  <Button variant="outline" onClick={disconnectGoogle} className="text-red-600 ml-auto" data-testid="disconnect-btn"><Unplug className="h-4 w-4 mr-1" />Disconnect</Button>
                </div>

                {events && events.length > 0 && (
                  <div className="mt-4 space-y-2" data-testid="pulled-events">
                    <div className="text-sm font-medium">Upcoming events in your Google Calendar</div>
                    {events.slice(0, 20).map(e => (
                      <div key={e.id} className="p-2 rounded border bg-muted/20 text-sm">
                        <div className="font-medium">{e.summary || '(no title)'}</div>
                        <div className="text-xs text-muted-foreground">{e.start}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
